import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * "Rolar até um alvo e piscar nele" — **uma** implementação, usada em três
 * lugares da tela de preview:
 *
 * 1. clique no número de uma questão no gabarito → vai até a questão;
 * 2. clique no cabeçalho "Questão N" → volta para o gabarito;
 * 3. depois de mover uma questão de posição → mostra onde ela foi parar.
 *
 * A chave é string (`question:<posição>`, `answer-key`) e não índice, porque o
 * mesmo registro serve para alvo que não é questão, e posição é o que o
 * gabarito conhece.
 *
 * `spotlight` guarda a chave em **estado** em vez de agir na hora, e isso é o
 * que faz o caso (3) funcionar: depois de mover a questão 5 para a 23, o
 * elemento que ocupa a posição 23 só existe no DOM **depois** do re-render com
 * a ordem nova — rolar dentro do `onSuccess` da mutation levaria até o card que
 * estava ali antes. Guardando a chave, o efeito roda depois do commit, com o
 * DOM já reordenado.
 */

/** Duração da piscada — tem de casar com o CSS de `question-spotlight`. */
const SPOTLIGHT_MS = 1600;

/**
 * Piso de espera pelo fim do scroll suave. `scrollend` dá o momento exato onde
 * existe (não existe no Safari), e este teto cobre o resto.
 */
const SCROLL_SETTLE_MS = 500;

interface QuestionSpotlightApi {
  /** `ref` callback: `ref={register('question:4')}`. */
  register: (key: string) => (el: HTMLElement | null) => void;
  spotlight: (key: string) => void;
}

const QuestionSpotlightContext = createContext<QuestionSpotlightApi | null>(
  null,
);

export function QuestionSpotlightProvider({
  children,
}: {
  children: ReactNode;
}) {
  const targets = useRef(new Map<string, HTMLElement>());
  const [pending, setPending] = useState<{ key: string; at: number } | null>(
    null,
  );

  const register = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) targets.current.set(key, el);
      else targets.current.delete(key);
    },
    [],
  );

  /**
   * `at` garante que pedir o MESMO alvo duas vezes seguidas pisque duas vezes
   * (chave igual sozinha não mudaria o estado, e o efeito não reexecutaria).
   *
   * O pedido **fica** no estado depois de atendido, em vez de ser zerado: zerar
   * reexecutaria o efeito, e a limpeza dele tiraria a classe no mesmo instante
   * — a piscada nunca aconteceria. Assim o efeito roda uma vez por pedido, e a
   * limpeza só acontece quando chega um pedido novo (que deve mesmo cancelar a
   * piscada anterior) ou no unmount.
   */
  const spotlight = useCallback(
    (key: string) => setPending({ key, at: Date.now() }),
    [],
  );

  useEffect(() => {
    if (!pending) return;
    const el = targets.current.get(pending.key);
    // Alvo que não existe (posição além do fim da prova, gabarito escondido):
    // desiste em vez de ficar tentando.
    if (!el) return;

    const box = el.getBoundingClientRect();
    const alreadyVisible = box.top >= 0 && box.bottom <= window.innerHeight;
    // `center` e não `start`: `start` esconde o alvo atrás do header fixo.
    if (!alreadyVisible) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    let flashTimer = 0;
    let started = false;
    // A piscada começa quando o scroll TERMINA. Começar junto faria metade dela
    // acontecer com o card ainda atravessando a tela — o oposto de "achei".
    const start = () => {
      if (started) return;
      started = true;
      el.classList.add('question-spotlight');
      flashTimer = window.setTimeout(
        () => el.classList.remove('question-spotlight'),
        SPOTLIGHT_MS,
      );
    };

    if (alreadyVisible) {
      start();
      return () => {
        window.clearTimeout(flashTimer);
        el.classList.remove('question-spotlight');
      };
    }

    document.addEventListener('scrollend', start, { once: true });
    const settle = window.setTimeout(start, SCROLL_SETTLE_MS);
    return () => {
      document.removeEventListener('scrollend', start);
      window.clearTimeout(settle);
      window.clearTimeout(flashTimer);
      el.classList.remove('question-spotlight');
    };
  }, [pending]);

  const api = useMemo(() => ({ register, spotlight }), [register, spotlight]);
  return (
    <QuestionSpotlightContext.Provider value={api}>
      {children}
    </QuestionSpotlightContext.Provider>
  );
}

/** Chave do gabarito — o destino do caminho de volta. */
export const ANSWER_KEY_ANCHOR = 'answer-key';

/** Chave de uma questão pela posição que ela ocupa **agora**. */
export function questionAnchor(position: number): string {
  return `question:${position}`;
}

export function useQuestionSpotlight(): QuestionSpotlightApi {
  const api = useContext(QuestionSpotlightContext);
  if (!api) {
    throw new Error(
      'useQuestionSpotlight precisa de um <QuestionSpotlightProvider> acima',
    );
  }
  return api;
}
